import { Box, Typography } from '@astro-ui/components'
import PecahBelah from 'assets/images/mdi_glass-fragile.svg'
import Telur from 'assets/images/egg-crack-fill.svg'
import Cair from 'assets/images/Cair.svg'
import Busuk from 'assets/images/Busuk_2.svg'
import styled from 'styled-components'
import icInstruction from 'assets/images/icInstruction.svg'
import icOrder from 'assets/images/icOrder.svg'

import { receiptSize } from './layout'

type FooterPropsType = {
  orderNote?: string
  deliveryInstruction?: string
  deliveryHandling?: {
    has_big_item: boolean
    is_eggs: boolean
    is_fragile: boolean
    is_melting: boolean
    is_refill: boolean
    is_rotten: boolean
  }
}

const Footer = ({ orderNote, deliveryInstruction, deliveryHandling }: FooterPropsType) => {
  const hasHandling = Object.values(deliveryHandling || {}).some(Boolean)

  const Instruction = () => (
    <Box
      width={hasHandling ? '50%' : '100%'}
      minWidth={0}
      overflow="hidden"
      borderRight={hasHandling ? '1px dashed black' : 'none'}
    >
      <Typography fontSize={receiptSize(28)} fontWeight={900} lineHeight={1.25} mb={receiptSize(8)}>
        Instruksi
      </Typography>
      <Box display="flex" flexDirection="column" gap={receiptSize(10)}>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="flex-start"
          gap={receiptSize(12)}
          paddingRight={receiptSize(6)}
        >
          <img
            src={icInstruction}
            style={{ width: receiptSize(18), height: receiptSize(18), flexShrink: 0 }}
            alt="icon-instruction"
          />
          <Typography
            fontSize={receiptSize(16)}
            fontWeight={600}
            lineHeight={1.25}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {deliveryInstruction || '--'}
          </Typography>
        </Box>
        <Box display="flex" flexDirection="row" alignItems="flex-start" gap={receiptSize(12)}>
          <img
            src={icOrder}
            style={{ width: receiptSize(18), height: receiptSize(18), flexShrink: 0 }}
            alt="icon-order"
          />
          <Typography
            fontSize={receiptSize(16)}
            fontWeight={600}
            lineHeight={1.25}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {orderNote || '--'}
          </Typography>
        </Box>
      </Box>
    </Box>
  )

  const iconMap = {
    is_eggs: Telur,
    is_rotten: Busuk,
    is_melting: Cair,
    is_fragile: PecahBelah,
  }

  const renderIcons = (status: Record<string, boolean>) => {
    // Some delivery handling flags (for example has_big_item and is_refill)
    // do not have a corresponding icon in this receipt template.
    const activeKeys = Object.keys(iconMap).filter((key) => status[key])
    const count = activeKeys.length

    if (count === 0) return []

    const icons: string[] = []
    activeKeys.forEach((key) => {
      const portion = Math.floor(4 / count)
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < portion; i++) {
        icons.push(iconMap[key as keyof typeof iconMap])
      }
    })
    while (icons.length < 4) {
      icons.push(iconMap[activeKeys[0] as keyof typeof iconMap])
    }

    return icons
  }
  const Handling = () => {
    const isEggsOrFragile = deliveryHandling?.is_eggs || deliveryHandling?.is_fragile
    const isMeltingOrRotten = deliveryHandling?.is_melting || deliveryHandling?.is_rotten
    const mudahCairDanPecah = isEggsOrFragile && isMeltingOrRotten
    const isOnlyRotten =
      deliveryHandling?.is_rotten &&
      !deliveryHandling?.is_melting &&
      !deliveryHandling?.is_eggs &&
      !deliveryHandling?.is_fragile
    const DEFAULT_TITLE = 'AWAS! BARANG'

    const dictionaryTitleAndSubtitles = [
      {
        title: DEFAULT_TITLE,
        subtitle: 'MUDAH PECAH',
      },
      {
        title: DEFAULT_TITLE,
        subtitle: 'MUDAH CAIR',
      },
      {
        title: DEFAULT_TITLE,
        subtitle: 'MUDAH BUSUK',
      },
      {
        title: 'AWAS! MUDAH',
        subtitle: 'PECAH & CAIR',
      },
    ]

    const [fragile, melting, rotten, liquid] = dictionaryTitleAndSubtitles

    let selectedDictionary = null
    if (mudahCairDanPecah) {
      selectedDictionary = liquid
    } else if (isEggsOrFragile) {
      selectedDictionary = fragile
    } else if (isOnlyRotten) {
      selectedDictionary = rotten
    } else if (isMeltingOrRotten) {
      selectedDictionary = melting
    }

    return (
      <Box
        display="flex"
        flexDirection="column"
        paddingLeft={receiptSize(16)}
        alignSelf="center"
        width="50%"
        minWidth={0}
        overflow="hidden"
      >
        <Typography
          fontSize={receiptSize(31)}
          fontWeight={800}
          lineHeight={1}
          letterSpacing={receiptSize(0.5)}
          textAlign="center"
        >
          {selectedDictionary?.title || DEFAULT_TITLE}
        </Typography>
        <Typography fontSize={receiptSize(32)} fontWeight={900} lineHeight={1} textAlign="center">
          {selectedDictionary?.subtitle || 'MUDAH PECAH'}
        </Typography>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          mt={receiptSize(8)}
        >
          {renderIcons(deliveryHandling || {}).map((icon, index) => (
            <IconHandling
              key={index /* eslint-disable-line react/no-array-index-key */}
              src={icon}
              alt=""
              aria-hidden="true"
              onError={(event) => {
                event.currentTarget.style.setProperty('visibility', 'hidden')
              }}
            />
          ))}
        </Box>
      </Box>
    )
  }
  let justifyContent = 'center'

  const withInstruction = Boolean(orderNote || deliveryInstruction)

  if (withInstruction && hasHandling) {
    justifyContent = 'space-between'
  } else if (withInstruction) {
    justifyContent = 'flex-start'
  }
  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent={justifyContent}
      padding={receiptSize(16)}
      border="1px solid black"
      borderRadius={receiptSize(12)}
      minHeight={0}
      overflow="hidden"
    >
      {withInstruction && <Instruction />}
      {hasHandling && <Handling />}
      {!withInstruction && !hasHandling && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          width="100%"
        >
          <Typography fontSize={receiptSize(28)} fontWeight={900} lineHeight={1.25}>
            #BelanjaHitunganMenit
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default Footer

const IconHandling = styled.img`
  width: ${receiptSize(46)};
  height: ${receiptSize(46)};
  flex: 0 0 ${receiptSize(46)};
  object-fit: contain;
`
